import {
    IonButton,
    IonCol,
    IonInput,
    IonItem,
    IonLabel,
    IonRow,
} from "@ionic/react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Horse } from "../../../models";
import { updateHorse } from "../../../utilities/horse/Horse";
import ErrorAlert from "../../Errors/ErrorAlert";
import HorseHeightInput from "../HorseHeightInput";
import { UpdateHorseInput } from "../../../API";
import SuccessBanner from "../../Banners/SuccessBanner";
import { Input, Label } from "reactstrap";
import { breeds } from "../../../utilities/horse/Breed";
import RequiredInputIndicator from "../../Forms/RequiredInputIndicator";
import Spinner from "../../Spinners/Spinner";

interface _Props {
    horse: Horse
}

const EditHorseBasicInfoForm: React.FC<_Props> = ({horse}) => {   
    const [currentHorse, setCurrentHorse] = useState<Horse | null | undefined>();
    const [name, setName] = useState("");
    const [nickname, setNickname] = useState( "");
    const [horseHeightInInches, setHorseHeigtInInches] = useState(0);
    const [horseHeightFraction, setHorseHeigtFraction] = useState("");
    const [yearBorn, setYearBorn] = useState("");
    const [breed, setBreed] = useState("");
    const [sex, setSex] = useState("");
    
    const [isLoading, setIsLoading] = useState(false);
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    const getHorseData = (horse?: Horse) => {
        setCurrentHorse(horse);
        setName(horse?.name || "");
        setNickname(horse?.nickname || "");
        setYearBorn(horse?.dateOfBirth ? moment(horse?.dateOfBirth).format("YYYY"): "");
        setBreed(horse?.breed || "");
        setSex(horse?.sex || "");
        setHorseHeigtInInches(horse?.height ? parseFloat(horse?.height) : 0);
        setHorseHeigtFraction(horse?.fraction || "");
        setError("");
        setSuccess("");
    }

    if (!horse || horse.id !== currentHorse?.id) getHorseData(horse);

    const verifyForm = () => {
        setSuccess("");
        setError("");
        if (!name) {
            setError("Please include a registered name for the horse. If the horse is not registered, please still include the horse's name in this field.");
            return false;
        }
        return true;
    }

    const handleHeightChange = (heightInInches: number, fraction: string) => {
        setHorseHeigtInInches(heightInInches);
        setHorseHeigtFraction(fraction);
    }

    const handleUpdateHorse = async () => {
       if (currentHorse?.id) {
        let input: UpdateHorseInput = {
            id: currentHorse.id,
            personId: currentHorse.personId || null,
            name: name || null,
            nickname : nickname || null,
            height: horseHeightInInches ? horseHeightInInches.toString() : null,
            fraction: horseHeightFraction || null,
            breed: breed || null,
            sex: sex || null
        };
        if (yearBorn) input.dateOfBirth = moment(yearBorn).format("YYYY-MM-DD");
        const updateResult = await updateHorse(input);
        if (updateResult.isSuccess) {
            setError("");
            setSuccess("Successfully saved the updates.");
        } else {
            setSuccess("");
            setError(updateResult.message);
        }
       } else {
        setError("No horse was found.");
       }
    }

    const handleEditHorse = async () => {
        setIsLoading(true);
        const isValid = verifyForm();
        if (isValid) {
            await handleUpdateHorse();
        }
        setIsLoading(false);
    }

    return (
        <>
            {isLoading ?
                <IonRow className="ion-justify-content-center">
                    <IonCol size="12" className="ion-text-center">
                        <Spinner />
                    </IonCol>
                </IonRow>
                :
                <>
                    {success && <SuccessBanner success={success} />}
                    {error && <ErrorAlert width="12" error={error} />}
                    <IonRow>
                        <IonCol sizeXs="12" sizeMd="6">
                            <IonItem color="white">
                                <IonLabel position="stacked">Registered Name<RequiredInputIndicator /></IonLabel>
                                <IonInput 
                                    type="text"
                                    value={name}
                                    aria-required={true}
                                    onIonChange={e => {
                                        setName(e.detail.value!);
                                    }}
                                />
                            </IonItem>
                        </IonCol>
                        <IonCol sizeXs="12" sizeMd="6">
                            <IonItem color="white">
                                <IonLabel position="stacked">Nickname</IonLabel>
                                <IonInput 
                                    type="text"
                                    value={nickname}
                                    aria-required={true}
                                    onIonChange={e => {
                                        setNickname(e.detail.value!);
                                    }}
                                />
                            </IonItem>
                        </IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol sizeXs="12" sizeMd="6">
                            <Label for="breedSelect">Breed</Label>
                            <Input 
                                type="select" 
                                name="select"
                                value={breed}
                                id="breedSelect"
                                onChange={e => setBreed(e.target.value)}
                            >
                                <option>-</option>
                                {breeds.map((breed, index) => (
                                    <option key={index}>{breed}</option>
                                ))}
                            </Input>
                        </IonCol>
                        <IonCol sizeXs="12" sizeMd="6">
                            <Label for="sexSelect">Sex</Label>
                            <Input 
                                type="select" 
                                name="select"
                                value={sex}
                                id="sexSelect"
                                onChange={e => setSex(e.target.value)}
                            >
                                <option>-</option>
                                <option>Gelding</option>
                                <option>Mare</option>
                                <option>Stallion</option>
                                <option>Colt</option>
                            </Input>
                        </IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol size="12">
                            <HorseHeightInput 
                                isRequired={false} 
                                heightInInches={horseHeightInInches} 
                                fraction={horseHeightFraction} 
                                onChange={(i, f) => handleHeightChange(i, f)}
                            />
                        </IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol sizeXs="12" sizeMd="6">
                            <IonItem color="white">
                                <IonLabel position="stacked">Year Born</IonLabel>
                                <IonInput 
                                    type="text"
                                    value={yearBorn}
                                    aria-required={true}
                                    onIonChange={e => {
                                        setYearBorn(e.detail.value!);
                                    }}
                                />
                            </IonItem>
                        </IonCol>
                    </IonRow>
                    <IonRow className="ion-justify-content-center">
                        <IonCol sizeMd="4" className="ion-text-center">
                            <IonButton
                                className="ion-margin-top"
                                color="tertiary"
                                expand="block"
                                onClick={handleEditHorse}
                            >
                                Save
                            </IonButton>
                        </IonCol>
                    </IonRow>
                </>
            }
        </>
    );
};

export default EditHorseBasicInfoForm;